import { Theme } from '@mui/material'
import { t } from 'i18next'
import logger, { levels, setLevel } from 'loglevel'
import moment from 'moment'
import { Log } from 'oidc-client-ts'
import { toast } from 'react-toastify'
import DOC from 'src/assets/svg/doc.svg'
import EXCEL from 'src/assets/svg/excel.svg'
import IMAGE from 'src/assets/svg/img.svg'
import PDF from 'src/assets/svg/pdf.svg'
import GZ from 'src/assets/svg/file.svg'
import packageJson from '../package.json'
import {
  INTERNAL_SERVER_ERROR,
  pageSizes,
  SUCCESSFULLY_CREATED,
  SUCCESSFULLY_DELETED,
  SUCCESSFULLY_UPDATED,
} from './constant'
import { IMainComponentType, RepeatType } from './enums'
import { IFile, IPositionDropDown, IPositionType } from './generated-types'
import { IMainComponent } from './generated-types/main-component'
import i18n from './i18n'
import { MUIThemeColors } from './mui-theme'
import { getLocalDateTime, getMainComponentStatus } from './utils/function'
import { compareByReference, compareCagesByReference, sortPositionsByReference } from './utils/sortByReference'
import ColorHash from 'color-hash'
export function setGlobalLogLevel() {
  Log.setLevel(logger.getLevel() as any)

  const logLevelEnv = process.env.REACT_APP_LOG_LEVEL?.toLowerCase() || 'info'

  switch (logLevelEnv) {
    case 'info':
      setLevel(levels.INFO)
      break
    case 'debug':
      setLevel(levels.DEBUG)
      break
    case 'error':
      setLevel(levels.ERROR)
      break
    case 'trace':
      setLevel(levels.TRACE)
      break
    case 'warn':
      setLevel(levels.WARN)
      break
  }
}

export const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB']
export function bytesToSize(bytes: number): string {
  if (bytes === 0) return 'n/a'
  const i: number = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString())
  if (i === 0) return `${bytes} ${sizes[i]}`
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

export const imageMimeTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/*']

export function isFileContentTypeImage(fileContentType: string) {
  const mimeType = fileContentType.split('.').pop()
  return imageMimeTypes.includes(mimeType!)
}

export function isFileUrlExtensionImage(fileUrl: string) {
  const regex = /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i
  return regex.test(fileUrl)
}

export function isFileImage(file: IFile) {
  if (!file) return false

  return imageMimeTypes.some(supportedMime => file.contentType?.includes(supportedMime))
}

export const contentType = {
  'application/pdf': { name: 'PDF', icon: PDF },
  'image/jpeg': { name: 'JPEG', icon: IMAGE },
  'image/png': { name: 'PNG', icon: IMAGE },
  'image/jpg': { name: 'JPG', icon: IMAGE },
  'image/svg+xml': { name: 'SVG', icon: IMAGE },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { name: 'Excel', icon: EXCEL },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { name: 'Word', icon: DOC },
  'application/gzip': { name: 'GZIP', icon: GZ },
}

export const caching = () => {
  const version = localStorage.getItem('version')
  if (version != packageJson.version) {
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name)
        })
      })

      window.location.reload()
    }

    localStorage.clear()
    localStorage.setItem('version', packageJson.version)
  }
}

export const getFormattedDate = (date?: Date, format?: string) => {
  return date ? moment(getLocalDateTime(date)).format(format ?? 'YYYY-MM-DD') : '-'
}

export const dateIsPassed = (firstDate?: Date, secondDate?: Date) => {
  return moment(firstDate).isBefore(secondDate)
}

/**
 * Get the start and end dates of the month for a given date
 * @param date - The date to get the month range for
 * @returns An object with startDate (first day of month) and endDate (last day of month)
 */
export const getMonthDateRange = (date: Date): { startDate: Date; endDate: Date } => {
  const startDate = new Date(date)
  startDate.setDate(1)

  const endDate = new Date(date)
  endDate.setMonth(endDate.getMonth() + 1)
  endDate.setDate(0)

  return { startDate, endDate }
}

/**
 * Check if two date ranges overlap
 * @param start1 - Start date of the first range (Date or string)
 * @param end1 - End date of the first range (Date or string)
 * @param start2 - Start date of the second range (Date or string)
 * @param end2 - End date of the second range (Date or string)
 * @returns true if the date ranges overlap, false otherwise
 */
export const doDateRangesOverlap = (
  start1: Date | string,
  end1: Date | string,
  start2: Date | string,
  end2: Date | string,
): boolean => {
  const startDate1 = new Date(start1)
  const endDate1 = new Date(end1)
  const startDate2 = new Date(start2)
  const endDate2 = new Date(end2)

  // Two date ranges overlap if: start1 <= end2 AND end1 >= start2
  return startDate1 <= endDate2 && endDate1 >= startDate2
}

export const shortenFileName = (fileName: string, maxLength: number): { fileName: string; isShorten: boolean } => {
  let isShorten = false
  const parts = fileName.split('.')
  const extension = parts.length > 1 ? parts.pop() : ''
  let fileNameWithoutExtension = parts.join('.')

  if (fileNameWithoutExtension.length > maxLength) {
    isShorten = true
    fileNameWithoutExtension = fileNameWithoutExtension.slice(0, maxLength) + '...'
  }

  return { fileName: `${fileNameWithoutExtension}${extension ? `.${extension}` : ''}`, isShorten: isShorten }
}

export const checkMod11 = (value: string) => {
  value = value.split(/\s|-/g).join('')
  if (value.length >= 9) {
    const numberArray = value.split('').map(Number)
    let controlNumber = 2,
      sumForMod = 0,
      i: number

    for (i = value.length - 2; i >= 0; --i) {
      sumForMod += numberArray[i] * controlNumber
      if (++controlNumber > 7) controlNumber = 2
    }
    const result = 11 - (sumForMod % 11)

    if ((result === 11 ? 0 : result) === parseInt(value.charAt(value.length - 1), 10)) {
      return true
    }
  }
}

export const equipmentSortingArray: number[] = [
  IMainComponentType.Ring,
  IMainComponentType.Net,
  IMainComponentType.LiceSkirt,
  IMainComponentType.Custom,
]

export const POSITION_TYPE_RANGES: Partial<Record<IPositionType, { min: number; max: number }>> = {
  [IPositionType.MooringLine]: { min: 101, max: 199 },
  [IPositionType.Corner]: { min: 301, max: 399 },
  [IPositionType.Bridle]: { min: 501, max: 599 },
  [IPositionType.FrameLines]: { min: 701, max: 799 },
  [IPositionType.Cage]: { min: 901, max: 999 },
}

export const ALL_POSITION_RANGES = [
  { min: 101, max: 199 },
  { min: 301, max: 399 },
  { min: 501, max: 599 },
  { min: 701, max: 799 },
  { min: 901, max: 999 },
]

export const isValidPositionNumber = (num: number): boolean => {
  return ALL_POSITION_RANGES.some(range => num >= range.min && num <= range.max)
}

export const handleToastMessage = (message?: string) => {
  if (!message) return
  if (message === SUCCESSFULLY_CREATED) {
    toast.success(t('addedSuccessfully', { ns: 'common' }))
  } else if (message === SUCCESSFULLY_UPDATED) {
    toast.success(t('updatedSuccessfully', { ns: 'common' }))
  } else if (message === SUCCESSFULLY_DELETED) {
    toast.success(t('deletedSuccessfully', { ns: 'common' }))
  } else if (message === INTERNAL_SERVER_ERROR) {
    toast.error(t('unExpectedError', { ns: 'common-api-message' }))
  }
}

export const validateCertificateResponse = (message: string) => {
  if (message == 'CannotAddCertificate') {
    toast(t('CannotAddCertificate', { ns: 'certificate' }), { type: 'error' })
  }
  if (message == 'ValidUntilLessThanCertificateDate') {
    toast(t('ValidUntilLessThanCertificateDate', { ns: 'certificate' }), { type: 'warning' })
  }
  if (message == 'CertificateDateGreaterThanValidUntil') {
    toast(t('CertificateDateGreaterThanValidUntil', { ns: 'certificate' }), { type: 'warning' })
  }
  if (message == 'NotAValidCertificate') {
    toast(t('NotAValidCertificate', { ns: 'certificate' }), { type: 'warning' })
  }
  if (message == 'NoValidCertificate') {
    toast(t('NoValidCertificate', { ns: 'service-station' }), { type: 'warning' })
  }
  if (message == 'CertificateNumberExists') {
    toast.error(t('certificateNumberExists', { ns: 'main-component' }))
  }
}

export const CompareTwoDatesWithoutTime = (date1: Date, date2: Date) => {
  const momentDate1 = moment(date1)
  const momentDate2 = moment(date2)

  // Check if the dates are equal (ignoring time)
  return !momentDate1.isSame(momentDate2, 'day')
}

// Check if the string is non-empty and does not contain only whitespace
export const validateString = (input: string) => {
  return input.trim().length > 0
}

export const getMonthName = (monthNumber: number) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const monthIndex = monthNumber - 1
  return months[monthIndex]
}

interface IFormatDaysToTextByDays {
  days: number
}

interface IFormatDaysToTextByDifference {
  startDate: Date
  endDate: Date
}

export const formatDaysToText = (props: IFormatDaysToTextByDays | IFormatDaysToTextByDifference): string => {
  let days = 0

  if (typeof (props as IFormatDaysToTextByDays).days === 'number') {
    days = (props as IFormatDaysToTextByDays).days
  } else {
    const { startDate, endDate } = props as IFormatDaysToTextByDifference
    if (startDate && endDate) {
      days = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    }
  }

  const start = moment()
  const endDate = moment(start).add(days, 'days')

  const years = endDate.diff(start, 'years')
  start.add(years, 'years')

  const months = endDate.diff(start, 'months')
  start.add(months, 'months')

  const remainingDays = endDate.diff(start, 'days')

  let result = ''
  if (years > 0) {
    result += `${years} ${t('year', { ns: 'common' })}${years > 1 ? 's' : ''} `
  }
  if (months > 0) {
    result += `${months} ${t('month', { ns: 'common' })}${months > 1 ? 's' : ''} `
  }
  if (remainingDays > 0) {
    result += `${remainingDays} ${t('day', { ns: 'common' })}${remainingDays > 1 ? 's' : ''} `
  }

  if (!result.trim()) {
    return `0 ${t('days', { ns: 'common' })}`
  }

  return result.trim()
}

export const getRecurrenceText = (repeatType, frequency, weekDays) => {
  if (!repeatType) return `${t('no-recurrence', { ns: 'common' })}`

  const weekdaysMap = {
    '0': `${t('Sunday', { ns: 'common' })}`,
    '1': `${t('Monday', { ns: 'common' })}`,
    '2': `${t('Tuesday', { ns: 'common' })}`,
    '3': `${t('Wednesday', { ns: 'common' })}`,
    '4': `${t('Thursday', { ns: 'common' })}`,
    '5': `${t('Friday', { ns: 'common' })}`,
    '6': `${t('Saturday', { ns: 'common' })}`,
  }

  switch (repeatType) {
    case RepeatType.Never:
      return `${t('no-recurrence', { ns: 'common' })}`

    case RepeatType.Daily:
      return frequency && frequency !== '1'
        ? `${t('every', { ns: 'common' })} ${frequency} ${t('day', { ns: 'common' })}`
        : `${t('every-day', { ns: 'common' })}`

    case RepeatType.Weekly:
      if (weekDays) {
        const days = weekDays.split(',').map(day => weekdaysMap[day.trim()] || day)
        if (days.length === 7) {
          return frequency && frequency !== '1'
            ? `${t('every', { ns: 'common' })} ${frequency} ${t('week-on-all-days', { ns: 'common' })}`
            : `${t('every-week-on-all-days', { ns: 'common' })}`
        }
        const frequencyText =
          frequency && frequency !== '1'
            ? `${t('every', { ns: 'common' })} ${frequency} ${t('weeks-on', { ns: 'common' })}`
            : `${t('every-week-on', { ns: 'common' })}`
        return days.length > 1 ? `${frequencyText} ${days.join(', ')}` : `${frequencyText} ${days[0]}`
      }
      return frequency && frequency !== '1'
        ? `${t('every', { ns: 'common' })} ${frequency} ${t('weeks', { ns: 'common' })}`
        : `${t('weekly', { ns: 'common' })}`

    case RepeatType.Monthly:
      return frequency && frequency !== '1'
        ? `${t('every', { ns: 'common' })} ${frequency} ${t('months', { ns: 'common' })}`
        : `${t('every-month', { ns: 'common' })}`

    case RepeatType.Yearly:
      return frequency && frequency !== '1'
        ? `${t('every', { ns: 'common' })} ${frequency} ${t('years', { ns: 'common' })}`
        : `${t('every-year', { ns: 'common' })}`

    default:
      return ''
  }
}

export const GetRGBA = (color, alpha) => {
  const r = parseInt(color.slice(1, 3), 16)
  const g = parseInt(color.slice(3, 5), 16)
  const b = parseInt(color.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const fileSize = contentLength => {
  let size = contentLength
  if (size < 1024) {
    return `${size} KB`
  }
  size = (size / 1024).toFixed(2)
  if (size < 1024) {
    return `${size} MB`
  }
  size = (size / 1024).toFixed(2)
  if (size < 1024) {
    return `${size} GB`
  }
}

export const getThemeColor = (theme: Theme, color: MUIThemeColors) => {
  return theme.palette[color][theme.palette.mode]
}

export const getPageSize = size => {
  let sizeReturn = 5
  pageSizes.forEach(sizeVal => {
    if (sizeVal > size) {
      sizeReturn = sizeVal
    }
  })
  return sizeReturn
}

export const setToMidnight = date => {
  const newDate = new Date(date)
  newDate.setHours(0, 0, 0, 0)
  return newDate
}

export { compareByReference, compareCagesByReference, sortPositionsByReference }

export function filterAndSortPositions(positions: IPositionDropDown[], filter: IPositionType[]) {
  const sortedPositions = [...positions].sort(sortPositionsByReference)
  if (filter.length == 0) return sortedPositions
  const filteredPositions = sortedPositions.filter(position => filter.includes(position.type))
  return filteredPositions
}

export const getTranslatedText = (name: string) => {
  return i18n.language == 'en' || i18n.language == 'us'
    ? JSON.parse(name as string).en ?? ''
    : JSON.parse(name as string).no ?? ''
}

export function getMedian(arr) {
  if (!Array.isArray(arr) || arr.length === 0) {
    throw new Error('Input must be a non-empty array')
  }

  // Sort the array in ascending order
  const sorted = arr.slice().sort((a, b) => a - b)

  const mid = Math.floor(sorted.length / 2)

  // If odd, return the middle element; if even, return the average of the two middle elements
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

export function getMidPoint(arr) {
  if (!Array.isArray(arr) || arr.length === 0) {
    throw new Error('Input must be a non-empty array')
  }

  const sorted = arr.sort((a, b) => a - b)
  const mid = (sorted[0] + sorted[sorted.length - 1]) / 2
  return mid
}

export const getImageColor = async (image: string): Promise<string | null> => {
  try {
    const response = await fetch(image)
    if (!response.ok) {
      return null
    }
    const blob = await response.blob()

    const img = new Image()
    img.src = URL.createObjectURL(blob)

    return new Promise((resolve, reject) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (ctx) {
          // Set canvas size to match the image
          canvas.width = img.width
          canvas.height = img.height

          // Draw the image onto the canvas
          ctx.drawImage(img, 0, 0)

          // Get the pixel color at the center
          const x = Math.floor(img.width / 2)
          const y = Math.floor(img.height / 2)
          const pixel = ctx.getImageData(x, y, 1, 1).data

          const color = `rgba(${pixel[0]}, ${pixel[1]}, ${pixel[2]}, ${pixel[3] / 255})`

          // Clean up
          URL.revokeObjectURL(img.src)

          resolve(color)
        } else {
          reject('Canvas context is not available')
        }
      }

      img.onerror = error => {
        reject(`Image failed to load: ${error}`)
      }
    })
  } catch (error) {
    console.error('Error downloading or processing the image:', error)
    return null
  }
}

export const getValidUntilDate = (startDate, duration) => {
  const newValidUntilDate = new Date(startDate)
  const newMonth = newValidUntilDate.getMonth() + duration
  if (newMonth > 11) {
    newValidUntilDate.setFullYear(newValidUntilDate.getFullYear() + Math.floor(newMonth / 12))
    newValidUntilDate.setMonth(newMonth % 12)
  } else if (newMonth < 0) {
    newValidUntilDate.setFullYear(newValidUntilDate.getFullYear() + Math.floor(newMonth / 12))
    newValidUntilDate.setMonth(newMonth % 12)
  } else {
    newValidUntilDate.setMonth(newMonth)
  }
  // handle if start date is 29 and new date is not leap year
  if (
    startDate.getMonth() % 2 == 0 &&
    startDate.getDate() > 28 &&
    newValidUntilDate.getMonth() == 2 &&
    !isLeapYear(newValidUntilDate.getFullYear())
  ) {
    newValidUntilDate.setMonth(1, 28)
  }
  // handle if start date if feb-29 on a leap year
  if (isLeapYear(startDate.getFullYear()) && startDate.getMonth() == 1 && startDate.getDate() == 29) {
    newValidUntilDate.setDate(29)
    // reset for full years and new date is not a leap year
    if (!isLeapYear(newValidUntilDate.getFullYear()) && duration % 12 == 0) newValidUntilDate.setMonth(1, 28)
  }
  return newValidUntilDate
}

const isLeapYear = year => {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

export const sortByName = (a, b) => {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
}

export function isJSON(str) {
  try {
    return JSON.parse(str) && !!str
  } catch (e) {
    return false
  }
}

export function isNumeric(string) {
  return /^[+-]?\d+(\.\d+)?$/.test(string)
}

export const extractPlainText = (html: string | null): string => {
  if (!html) return ''

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const decoded = doc.documentElement.textContent || ''

  return decoded.replace(/<\/?[^>]+(>|$)/gi, '')
}

const handleSelectOptions = <T extends { id: number | string }>(
  initialItems: T[],
  selectedItem: T,
  allItems: T[],
): T[] => {
  if (initialItems.find(x => x.id == selectedItem.id) != null) {
    return initialItems.filter(x => x.id != selectedItem.id)
  } else {
    return [...initialItems, allItems.find(x => x.id == selectedItem.id)!]
  }
}

const handleRowSelectFunc = <T extends { id: number | string }, E extends Element = Element>(
  selectedItems: T[],
  selectedItem: T,
  allItems: T[],
  event: React.MouseEvent<E, MouseEvent>,
): T[] => {
  if (event.ctrlKey || event.metaKey) {
    //select multiple positions
    return handleSelectOptions(selectedItems, selectedItem, allItems)
  } else if (event.shiftKey) {
    const prevId = selectedItems[selectedItems.length - 1]?.id
    //1st shift click
    if (prevId == undefined) {
      return handleSelectOptions([], selectedItem, allItems)
    }
    const allPositions = allItems
    const startIndex = allPositions.findIndex(x => x.id == prevId)
    const endIndex = allPositions.findIndex(x => x.id == selectedItem.id)
    //select all positions between start and end index
    const selectedRange = allPositions.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1)
    const selectedArray = [...selectedItems, ...selectedRange]
    //Remove duplicates
    return selectedArray.filter((item, index, self) => index === self.findIndex(t => t.id === item.id))
  } else {
    //select single position
    if (selectedItems.length == 1 && selectedItems[0].id == selectedItem.id) {
      return []
    } else {
      return handleSelectOptions([], selectedItem, allItems)
    }
  }
}

const getDaysPassed = (scheduledDate?: Date, currentDate?: Date): number => {
  if (!scheduledDate || !currentDate) return 0

  const localScheduledDate = getLocalDateTime(scheduledDate)
  const localCurrentDate = getLocalDateTime(currentDate)

  if (!localScheduledDate || !localCurrentDate) return 0

  localScheduledDate.setHours(0, 0, 0, 0)
  localCurrentDate.setHours(0, 0, 0, 0)

  const diffTime = localCurrentDate.getTime() - localScheduledDate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))

  return diffDays > 0 ? diffDays : 0
}

const convertToUTCMidnightString = (date: string) => {
  const localDate = moment(date) // Local time

  // Convert to UTC midnight
  const utcMidnight = moment.utc({
    year: localDate.year(),
    month: localDate.month(),
    day: localDate.date(),
  })

  // Get UTC string with "Z"
  const utcString = utcMidnight.toISOString()
  return utcString
}

export const getEquipmentTooltipData = (mainComponent: IMainComponent, hiddenItems: string[] = []) => {
  const positionReference = mainComponent.position?.reference?.trim()
  const equipmentReference = (mainComponent as { reference?: string }).reference?.trim()
  const reference = equipmentReference || positionReference

  return [
    {
      key: 'serialNumber',
      name: t('serialNumber', { ns: 'main-component' }),
      value: mainComponent.serialNumber,
      hidden: hiddenItems.includes('serialNumber'),
    },
    {
      key: 'reference',
      name: t('reference', { ns: 'main-component' }),
      value: reference || '-',
      hidden: hiddenItems.includes('reference') || !reference,
    },
    {
      key: 'note',
      name: t('note', { ns: 'common' }),
      value: mainComponent.position?.note?.trim() || '-',
      hidden: hiddenItems.includes('note') || !mainComponent.position?.note?.trim(),
    },
    {
      key: 'description',
      name: t('description', { ns: 'common' }),
      value: mainComponent?.description?.length > 0 ? mainComponent.description : '-',
      hidden: hiddenItems.includes('description'),
    },
    {
      key: 'supplier',
      name: t('supplier', { ns: 'common' }),
      value: mainComponent.supplier ? mainComponent.supplier.name : '-',
      hidden: hiddenItems.includes('supplier'),
    },
    {
      key: 'status',
      name: t('status', { ns: 'main-component' }),
      value: t(getMainComponentStatus(mainComponent as IMainComponent), { ns: 'main-component' }),
      hidden: hiddenItems.includes('status'),
    },
  ]
}

export const formatRelativeTime = (dateString: string): string => {
  const normalized = /Z|[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : dateString + 'Z'
  const date = new Date(normalized)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMinutes < 1) return 'just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return getFormattedDate(date, 'YYYY-MM-DD')
}

export const formatRemainingTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return minutes > 0 ? `${minutes} min ${secs} seconds` : `${secs} seconds`
}

export const coreUtils = {
  setGlobalLogLevel,
  bytesToSize,
  isFileContentTypeImage,
  isFileUrlExtensionImage,
  isFileImage,
  caching,
  getFormattedDate,
  dateIsPassed,
  getMonthDateRange,
  shortenFileName,
  checkMod11,
  handleToastMessage,
  validateCertificateResponse,
  CompareTwoDatesWithoutTime,
  validateString,
  getMonthName,
  formatDaysToText,
  GetRGBA,
  fileSize,
  getThemeColor,
  getPageSize,
  setToMidnight,
  filterAndSortPositions,
  compareByReference,
  compareCagesByReference,
  sortPositionsByReference,
  getMedian,
  getMidPoint,
  getImageColor,
  getValidUntilDate,
  sortByName,
  isJSON,
  handleRowSelectFunc,
  getDaysPassed,
  convertToUTCMidnightString,
  getEquipmentTooltipData,
}

export type ColorUser =
  | {
      fullname: string
      userId: number
      [key: string]: any
    }
  | {
      firstName: string
      lastName: string
      userId: number
      [key: string]: any
    }

export type ColorName =
  | {
      fullname: string
      [key: string]: any
    }
  | {
      firstName: string
      lastName: string
      [key: string]: any
    }

const colorHash = new ColorHash()
/**
 * Returns a unique color for a user based on their name and user ID.
 *
 * Requires a user object with a userId and either:
 * - fullname
 * - firstName and/or lastName
 */

export const userColor = (user: ColorUser): string => {
  const name = user.fullname ? user.fullname : (user.firstName || '') + (user.lastName || '')
  return colorHash.hex((name || '').replace(/\s/g, '') + user.userId)
}

export const fontColorFromBackground = hexcolor => {
  const r = parseInt(hexcolor.substring(1, 3), 16)
  const g = parseInt(hexcolor.substring(3, 5), 16)
  const b = parseInt(hexcolor.substring(5, 7), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 128 ? 'black' : 'white'
}
